import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetAssessmentsAccumulationMappings,
  useGetAssessmentsComparison,
} from '@/entities/assessments/api/generated/api';
import { useApiErrorHandler } from '@/shared/hooks/useApiErrorHandler';
import { useAmountUnitStore } from '@/shared/stores/amount-unit-store';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { Button, Skeleton, useSkeletonMinDisplay } from '@/shared/ui';
import { SideMenuHeader } from '@/widgets/right-side-menu/ui/SideMenuHeader';
import { AIAssessmentCard } from './AIAssessmentCard';
import { AssessmentComparisonAccordion } from './AssessmentComparisonAccordion';
import { AssessmentComparisonTagSection } from './AssessmentComparisonTagSection';
import { buildPlaceholderData, flattenAccumulations } from './findAccumulationFromComparison';
import { TargetHeader } from './TargetHeader';
/**
 * 歳出比較・査定の右サイドパネルコンテンツ
 *
 * 一覧データのキャッシュから即座にコンテンツを表示し、
 * 詳細APIの応答を待たずにユーザー体験を向上させる。
 * 詳細API応答後にmemo等の追加データで更新する。
 */
export const AssessmentComparisonSidePanel: FC = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams({ from: '/_protected/assessment/$assessmentId/diff' });
  const search = useSearch({ from: '/_protected/assessment/$assessmentId/diff' });
  const accumulationId = search?.accumulationId;
  const amountUnit = useAmountUnitStore((state) => state.amountUnit);
  const { t } = useTranslation();
  const requestClose = useRouteSidePanelStore((state) => state.requestClose);

  // 一覧ページと同じクエリキーを使うため、キャッシュがあれば即座にデータ取得。
  // getQueryData と異なりリアクティブなので、URLパラメータで直接アクセスした場合も
  // 一覧データ到着後に自動的にプレースホルダーが構築される。
  const { data: comparisonData } = useGetAssessmentsComparison(assessmentId);

  // 階層走査は comparisonData が変わった時のみ再計算
  const allAccumulations = useMemo(
    () => (comparisonData ? flattenAccumulations(comparisonData.projects) : []),
    [comparisonData],
  );

  const placeholderData =
    comparisonData && accumulationId
      ? buildPlaceholderData({ allAccumulations, accumulationId, comparisonData })
      : null;

  const {
    data: accumulationMappingsData,
    isLoading: isAccumulationMappingsLoading,
    error: accumulationMappingsError,
  } = useGetAssessmentsAccumulationMappings(accumulationId ?? '', {
    query: { enabled: !!accumulationId },
  });

  const showSkeleton = useSkeletonMinDisplay(isAccumulationMappingsLoading && !placeholderData);

  useApiErrorHandler({
    error: accumulationMappingsError,
    navigate,
  });

  const displayData = accumulationMappingsData ?? placeholderData;

  if (!accumulationId || !displayData) {
    return null;
  }

  const alreadyExecutionAIAssessment: boolean = !!displayData.mappings.length;
  const currentIndex = allAccumulations.findIndex((item) => item.accumulationId === accumulationId);
  const previousAccumulationId =
    currentIndex > 0 ? allAccumulations[currentIndex - 1]?.accumulationId : undefined;
  const nextAccumulationId =
    currentIndex >= 0 && currentIndex < allAccumulations.length - 1
      ? allAccumulations[currentIndex + 1]?.accumulationId
      : undefined;

  return showSkeleton ? (
    <div className="space-y-4 p-4">
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  ) : (
    <div className="space-y-6">
      <SideMenuHeader
        onClose={() => {
          requestClose();
          navigate({ to: '.', search: { accumulationId: undefined } });
        }}
        additionalMenuItems={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                navigate({ to: '.', search: { accumulationId: previousAccumulationId } });
              }}
              disabled={!previousAccumulationId}
              aria-label={t('assessment.messages.go-to-previous')}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                navigate({ to: '.', search: { accumulationId: nextAccumulationId } });
              }}
              disabled={!nextAccumulationId}
              aria-label={t('assessment.messages.go-to-next')}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </>
        }
      />

      <div className="space-y-6 px-4">
        <TargetHeader accumulationMappingsData={displayData} />
        <AssessmentComparisonTagSection
          assessmentsAccumulationId={displayData.assessments_accumulation_id}
          initialAccumulationTags={displayData.accumulation_tags}
          isLoading={!accumulationMappingsData}
        />
        <AIAssessmentCard
          alreadyExecutionAIAssessment={alreadyExecutionAIAssessment}
          comment={displayData.comment}
        />
        <AssessmentComparisonAccordion
          accumulationMappingsData={displayData}
          amountUnit={amountUnit}
          isMemoLoading={!accumulationMappingsData}
        />
      </div>
    </div>
  );
};
